// External libraries
import type { ChangeEvent, FocusEvent } from 'react';
import React from 'react';
import { useStoreActions, useStoreState } from '@/store';
// @ts-expect-error untyped import
import { PhoneInput } from '@checkout/react-components';

// Internal modules
import { features, getContent } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import { PhoneContainer } from '../PhoneContainer';
import { PhoneConfirmationText, phoneInputStyleOverrides } from './styles';
import { CupOtpMessage } from '../CupOtpMessage';
import { DialingCodeSelect } from '../DialingCodeSelect';
import { PhoneType } from '../PhoneType';
import { useChangeHandlerFactory } from './hooks';
import { LoadingElement, MobileSpot } from '../../../Common';
import { InfoMessage } from '../../../Common/PageMessages';
import { getMobileHintStyles, getPhoneConfirmationContentKey } from './utils';
import { HiddenElementA11y } from '@/components/Common/StyledComponents';

interface PhoneNumberProps {
  blurHandler?: (e: FocusEvent<HTMLInputElement, Element>) => void;
  changeHandler: (e: ChangeEvent<HTMLInputElement>) => void;
  focusHandler?: (e: FocusEvent<HTMLInputElement, Element>) => void;
  isStreamlineExperience?: boolean;
}

export const PhoneNumber = (props: PhoneNumberProps): JSX.Element => {
  useMountLogging('PhoneNumber');
  const [isFocused, setIsFocused] = React.useState(false);
  const fiType = useStoreState(({ ui }) => ui.fiType);
  const phoneFieldValue = useStoreState(({ form }) => form.phone.value);
  const dialingCode = useStoreState(({ form }) => form.dialingCode);
  const phone = useStoreState(({ form }) => form.phone);
  const phoneType = useStoreState(({ form }) => form.phoneType);
  const cardType = useStoreState(({ form }) => form.cardNumber.cardType);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const hasCupOneTimePasswordConfirmationMessage = useStoreState(
    ({ ui }) => ui.hasCupOneTimePasswordConfirmationMessage,
  );
  const {
    dialingCodeSelectOptions = [],
    phoneMeta,
    territoriesToDialingCodeMap,
  } = useStoreState(({ api }) => api.griffinMetadata);
  const updateNonAddressField = useStoreActions(
    ({ form }) => form.updateNonAddressField,
  );
  const dialingCodeNum = territoriesToDialingCodeMap[dialingCode.value];
  const dialingCodeLabel = getContent('accessibility.dialingCode', {
    dialingCode: `+${dialingCodeNum}`,
  });
  const isAllowChangePhoneType = features.isActive('allowChangePhoneType');
  const canDialingCodeChange = features.isActive('canDialingCodeChange');
  const shouldShowSCAPhoneConfirmation = features.isActive(
    'shouldShowSCAPhoneConfirmation',
  );
  const optionalSignupAndStreamline = features.isActive(
    'isStreamlineOptionalExperience',
  );
  const theme = features.get('rebrandFormFieldsTheme');
  const isStreamlineExperience = props.isStreamlineExperience;

  const isCupOtpFlow =
    hasCupOneTimePasswordConfirmationMessage && cardType === 'CHINA_UNION_PAY';
  // We show the Phone Type dropdown if isAllowChangePhoneType is true and
  // it is not a cup otp flow and not the streamline experience.
  const withPhoneType =
    isAllowChangePhoneType && !isCupOtpFlow && !isStreamlineExperience;

  const { changeHandler, ...remainingDialingCodeProps } = props;
  const dialingCodeChangeHandler = useChangeHandlerFactory({
    propsChangeHandler: changeHandler,
  });

  /*
    Logic from line 61 to 80 used for displaying
    mobile hint language to the user in PWDless flows.
    Design team required to show hit on mobile field focus,
    we need to track focus state within <PhoneNumber/>, that is
    why we introduced new focusHandler, blurHandler callbacks,
    and isFocused component level state.
  */
  const getMobileNumberHintLang = features.isActive(
    'shouldShowMobileNumberHintLang',
  );

  const focusHandler = (e: FocusEvent<HTMLInputElement, Element>) => {
    props.focusHandler?.(e);
    setIsFocused(true);
  };

  const blurHandler = (e: FocusEvent<HTMLInputElement, Element>) => {
    props.blurHandler?.(e);
    setIsFocused(false);
  };
  const phoneNumberProps = {
    ...props,
    blurHandler,
    focusHandler,
  };
  /*
    Since BNPL is not PWDless at the moment
    we do not want to show mobile hint language
    to the user when they fall into BNPL flow
  */
  const isBNPLFlow = fiType === 'bnpl';
  const showMobileNumberHintLang = Boolean(
    !isBNPLFlow &&
      getMobileNumberHintLang &&
      (isFocused || phoneFieldValue.length > 0),
  );

  const shouldShowPhoneConfirmationSCAInfo = Boolean(
    shouldShowSCAPhoneConfirmation && !optionalSignupAndStreamline,
  );
  const shouldShowMobileHintLanguage = Boolean(showMobileNumberHintLang);

  // Wait to render the phone input until we have the phone metadata
  if (!phoneMeta?.mask?.length) {
    return <LoadingElement />;
  }

  // Update phoneType to mobile if it is a cup otp flow as it requires
  // SMS otp confirmation.
  if (isCupOtpFlow && phoneType.value !== 'MOBILE') {
    updateNonAddressField({
      field: 'phoneType',
      payload: {
        displayError: false,
        error: '',
        errorCode: '',
        isValid: true,
        value: 'MOBILE',
      },
    });
  }

  return (
    <>
      <PhoneContainer
        canDialingCodeChange={canDialingCodeChange}
        isStreamlineExperience={isStreamlineExperience}
        phone={phone}
        phoneType={phoneType}
        withPhoneType={withPhoneType}
      >
        {isCupOtpFlow && <CupOtpMessage />}

        {withPhoneType && (
          <PhoneType
            canDialingCodeChange={canDialingCodeChange}
            disabled={isFormSubmitting}
            {...props}
            {...phoneType}
          />
        )}

        {canDialingCodeChange && (
          <DialingCodeSelect
            changeHandler={dialingCodeChangeHandler}
            dialingCodeSelectOptions={dialingCodeSelectOptions}
            disabled={isFormSubmitting}
            {...dialingCode}
            {...remainingDialingCodeProps}
          />
        )}
        <>
          <PhoneInput
            {...(withPhoneType && phoneInputStyleOverrides)}
            {...phone}
            {...phoneMeta}
            {...phoneNumberProps}
            dialingCodeLabel={dialingCodeLabel}
            data-testid="phone"
            dialingCode={dialingCodeNum}
            disabled={isFormSubmitting}
            displayError={!withPhoneType && phone.displayError}
            focusedClassName="is-focused"
            hasError={withPhoneType && phone.displayError}
            label={
              isCupOtpFlow || isStreamlineExperience
                ? getContent('fieldLabels.mobilePhone')
                : getContent('fieldLabels.phoneNumber')
            }
            maxLength={phoneMeta.mask.length}
            required={true}
            ariaDescribedBy="accessibility-hint mobile-hint"
            theme={theme}
          />
          <HiddenElementA11y id="accessibility-hint">
            {dialingCodeLabel}
          </HiddenElementA11y>
        </>
        {shouldShowPhoneConfirmationSCAInfo && (
          <PhoneConfirmationText data-testid="phoneconfirmation">
            {getContent(getPhoneConfirmationContentKey())}
          </PhoneConfirmationText>
        )}
      </PhoneContainer>
      {shouldShowMobileHintLanguage && (
        <InfoMessage
          icon={MobileSpot}
          message={getContent('messages.mobileNumberHintLangPWDless')}
          styleOverrides={getMobileHintStyles()}
          testId="page-level-info-mobile-hint"
          id="mobile-hint"
        />
      )}
    </>
  );
};
